import { Component, computed, Signal, signal } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { ReactiveFormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe } from '@ngx-translate/core';

export enum ProgressDialogStepState {
  WAITING = 'waiting',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
  SKIPPED = 'skipped',
}

export class ProgressDialogStep {
  state: ProgressDialogStepState;
  title: string;
  subtitle?: string;
  icon?: string;
  iconColor?: string;

  showProgress?: boolean;
  progress?: number;

  constructor(state: ProgressDialogStepState, title: string) {
    this.state = state;
    this.title = title;
  }

  private clone(): ProgressDialogStep {
    let copy = new ProgressDialogStep(this.state, this.title);
    copy.subtitle = this.subtitle;
    copy.icon = this.icon;
    copy.iconColor = this.iconColor;
    copy.showProgress = this.showProgress;
    copy.progress = this.progress;
    return copy;
  }

  setState(state: ProgressDialogStepState) {
    let copy = this.clone();
    copy.state = state;
    return copy;
  }

  setTitle(title: string) {
    let copy = this.clone();
    copy.title = title;
    return copy;
  }

  setSubtitle(subtitle: string) {
    let copy = this.clone();
    copy.subtitle = subtitle;
    return copy;
  }

  setIcon(icon: string) {
    let copy = this.clone();
    copy.icon = icon;
    return copy;
  }

  setIconColor(iconColor: string) {
    let copy = this.clone();
    copy.iconColor = iconColor;
    return copy;
  }

  setShowProgress(showProgress: boolean) {
    let copy = this.clone();
    copy.showProgress = showProgress;
    return copy;
  }

  setProgress(progress: number) {
    let copy = this.clone();
    copy.progress = progress;
    return copy;
  }

  static equals(a: ProgressDialogStep, b: ProgressDialogStep) {
    return (
      a.state === b.state &&
      a.title === b.title &&
      a.subtitle === b.subtitle &&
      a.icon === b.icon &&
      a.iconColor === b.iconColor &&
      a.showProgress === b.showProgress &&
      a.progress === b.progress
    );
  }
}

export interface ProgressDialogData {
  steps: Signal<ProgressDialogStep[]>;
}

export function newProgressDialogStep(step: ProgressDialogStep) {
  return signal(step, {
    equal: ProgressDialogStep.equals,
  });
}

@Component({
  selector: 'progressDialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
  ],
  providers: [DialogService],
  templateUrl: './progressDialog.html',
  styleUrl: './progressDialog.scss',
})
export class ProgressDialog {
  showCloseButton = signal(false);

  constructor(
    public dialogRef: DynamicDialogRef<ProgressDialog>,
    public config: DynamicDialogConfig<ProgressDialogData>,
  ) {
    config.data.steps = config.data.steps || signal([]);
  }

  finishDialog(timeout: number = 500) {
    return new Promise<void>((resolve, reject) => {
      if (timeout != -1) {
        setTimeout(() => {
          this.dialogRef.close();
          resolve();
        }, timeout);
      } else {
        this.showCloseButton.set(true);
        resolve();
      }
    });
  }
}

export function openProgressDialog(dialog: DialogService, steps: Signal<ProgressDialogStep>[]) {
  let computedSteps = computed(
    () => {
      console.log('recompute');
      return steps.map((s) => s());
    },
    {
      equal: (a, b) => {
        if (a.length! !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
          if (!ProgressDialogStep.equals(a[i], b[i])) return false;
        }

        return true;
      },
    },
  );

  return dialog.open(ProgressDialog, {
    //minWidth: "200px",
    showHeader: false,
    width: '400px',
    data: { steps: computedSteps },
  });
}
